import { Button, EmptyState, ErrorState, Loading, PinPad } from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSession } from '@/app/session';
import { openTerminalSession } from '@/lib/api';
import { useDeviceStore } from '@/lib/deviceStore';
import type { Row } from '@/lib/db';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { supabase } from '@/lib/supabase';
import { AuthFrame } from './AuthFrame';
import './auth.css';

type PickerEmployee = Pick<
  Row<'employee'>,
  'id' | 'display_name' | 'code' | 'pin_set_at' | 'status'
>;

/** Up to two initials of a name, for the face on the picker. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => Array.from(w)[0] ?? '')
    .join('')
    .toUpperCase();
}

/** Employee picker + PIN pad on an enrolled device. Opens a terminal session server-side. */
export function LockPage() {
  const s = useSession();
  const nav = useNavigate();
  const device = useDeviceStore();
  // ADR-037: opening a session checks the PIN on the server; the pad waits for the connection
  const online = useOnline();
  const [selected, setSelected] = useState<PickerEmployee | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const employees = useQuery({
    queryKey: ['picker-employees', s.me?.terminal?.location_id],
    enabled: s.me?.principal === 'device',
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee')
        .select('id, display_name, code, pin_set_at, status')
        .eq('status', 'active')
        .order('display_name');
      if (error) throw error;
      return data;
    },
    refetchOnWindowFocus: true,
  });

  const submit = useCallback(
    async (pin: string) => {
      if (!selected) return;
      setBusy(true);
      setError(null);
      try {
        const r = await openTerminalSession(selected.id, pin);
        if (!r.ok) {
          const msg: Record<string, string> = {
            pin_invalid:
              r.attempts_left !== undefined
                ? `Wrong PIN. ${r.attempts_left} attempts left.`
                : 'Wrong PIN.',
            pin_locked: `Too many attempts. Try again in ${Math.ceil((r.retry_after_seconds ?? 60) / 60)} min.`,
            pin_not_set: 'No PIN set for this employee yet. Ask the owner to set one.',
            employee_suspended: 'This account is suspended.',
            not_assigned: 'Not assigned to this location.',
            terminal_revoked: 'This terminal has been revoked.',
          };
          setError(msg[r.code ?? ''] ?? 'Could not open a session.');
          setAttempt((n) => n + 1);
          return;
        }
        device.setSession(
          r.session_token!,
          { id: selected.id, display_name: selected.display_name },
          r.session!.expires_at,
        );
        await s.refresh();
        nav(r.landing ?? '/', { replace: true });
      } catch (x) {
        setError(errorText(x));
      } finally {
        setBusy(false);
      }
    },
    [selected, device, s, nav],
  );

  if (s.ready && !s.authed) return <Navigate to="/enroll" replace />;
  if (s.ready && s.me?.principal === 'human') return <Navigate to="/" replace />;
  if (s.ready && s.me?.principal === 'device' && s.me.terminal_session)
    return <Navigate to={s.me.terminal?.landing ?? '/'} replace />;
  if (s.ready && s.me?.principal === 'device' && s.me.terminal?.status === 'revoked') {
    return (
      <AuthFrame
        title="Terminal revoked"
        subtitle={`${s.me.terminal.name} (${s.me.terminal.code})`}
      >
        <div className="d-stack">
          <p className="d-muted">
            An administrator revoked this device. It must be enrolled again with a new code.
          </p>
          <Button
            variant="primary"
            size="lg"
            block
            onClick={() => {
              device.clear();
              void s.signOut().then(() => nav('/enroll'));
            }}
          >
            Enrol again
          </Button>
        </div>
      </AuthFrame>
    );
  }

  const t = s.me?.terminal;
  return (
    <AuthFrame
      title={selected ? selected.display_name : 'Who is working?'}
      subtitle={t ? `${t.name} · ${t.code}` : undefined}
    >
      {!selected ? (
        <div className="d-stack">
          {employees.isLoading && <Loading label="Loading staff…" />}
          {employees.isError && (
            <ErrorState
              title="Could not load the staff list"
              message={errorText(employees.error)}
              onRetry={() => void employees.refetch()}
            />
          )}
          <div className="picker" data-testid="employee-picker">
            {(employees.data ?? []).map((e) => (
              <button
                key={e.id}
                type="button"
                className="picker__btn lock__pick"
                onClick={() => {
                  setSelected(e);
                  setError(null);
                }}
                disabled={!e.pin_set_at}
                title={e.pin_set_at ? '' : 'No PIN set'}
                data-testid={`pick-${e.id}`}
              >
                <span className="lock__avatar" aria-hidden>
                  {initials(e.display_name)}
                </span>
                <span className="lock__name">{e.display_name}</span>
                {!e.pin_set_at && <span className="lock__nopin">no PIN</span>}
              </button>
            ))}
          </div>
          {employees.data?.length === 0 && (
            <EmptyState title="No staff here yet">
              No employees are assigned to this location yet.
            </EmptyState>
          )}
        </div>
      ) : (
        <div className="d-stack">
          <div className="lock__who">
            <span className="lock__avatar lock__avatar--lg" aria-hidden>
              {initials(selected.display_name)}
            </span>
          </div>
          <PinPad
            key={attempt}
            title="Enter PIN"
            onSubmit={(pin) => void submit(pin)}
            onCancel={() => {
              setSelected(null);
              setError(null);
            }}
            disabled={busy || !online}
            error={error}
          />
        </div>
      )}
    </AuthFrame>
  );
}
